import remove = require("lodash/remove");
import findIndex = require("lodash/findIndex");
import first = require("lodash/first");
import isString = require("lodash/isString");
import isEmpty = require("lodash/isEmpty");
import GridConfig from 'core/grid/domain/gridConfig';
import GridConfigCondition from 'core/grid/domain/gridConfigCondition';
import {lovLabel, IDomainObject, lovValue} from "core/form/formUtils";
import {compare} from "core/common/utils/utils";
import MdEntity from "core/metamodel/mdEntity";
import MdField from "core/metamodel/mdField";
import {ID, PRIMITIVE} from "core/metamodel/metaConstant";
import {observable, action, computed, ObservableMap, IMapEntry} from "mobx";
import {LovItem} from "core/metamodel/mdEntity";
import gridService from 'core/grid/gridService';
import {DomainAncestor, entityConfig, fieldInfo} from "core/common/domain/DomainAncestor";
import {Movable} from "core/grid/gridUtils";
import GridConfigSort from "core/grid/domain/gridConfigSort";
import moment from "moment";
import {Moment} from "moment";
import {MAX_INTEGER} from "core/common/utils/constant";
import {loc} from "core/common/config/locales";
import {Error} from "tslint/lib/error";
import {LocaleLabel} from "core/metamodel/metaDomain";
import {splitByLastSubstring} from "core/common/utils/stringUtils";

export enum GRIDTYPE {
  HIBERNATESQLQUERY = "HIBERNATESQLQUERY",
  JAVACONFIG = "JAVACONFIG",
  SQLQUERY = "SQLQUERY"
}

// COUNTRIES
export enum FILTEROPERATOR {
  IN = "IN",
  NOTIN = "NOTIN",
  EQUAL = "EQUAL",
  NOTEQUAL = "NOTEQUAL",
  STARTSWITH = "STARTSWITH",
  NOTSTARTSWITH = "NOTSTARTSWITH",
  ENDSWITH = "ENDSWITH",
  NOTENDSWITH = "NOTENDSWITH",
  GREATERTHAN = "GREATERTHAN",
  GREATEROREQUALTHAN = "GREATEROREQUALTHAN",
  LESSTHAN = "LESSTHAN",
  LESSOREQUALTHAN = "LESSOREQUALTHAN",
  CONTAINS = "CONTAINS",
  NOTCONTAINS = "NOTCONTAINS",
  LASTXDAYS = "LASTXDAYS",
  NEXTXDAYS = "NEXTXDAYS",
  FROMTO = "FROMTO",
  ISTRUE = "ISTRUE",
  ISFALSE = "ISFALSE",
  ISNULL = "ISNULL",
  ISNOTNULL = "ISNOTNULL"
}

export enum AGGREGATETYPE {
  SUM = "SUM",
  AVG = "AVG"
}

export const AGGREGATETYPE_MAP: {[key in AGGREGATETYPE] : {glyph: string}} = {
  SUM: {glyph: '\u03A3'},
  AVG: {glyph: '\u2300'}
};

type TFilterFunction = (field: MdField, value: any, params: any[]) => boolean;

const FILTEROPERATOR_MAP: {[key: string]: TFilterFunction} = {
  IN: (field: MdField, value: any, params: any[]) => params.map(p => lovValue(p)).includes((field.valueSourceType) ? lovValue(value) : value),
  NOTIN: (field: MdField, value: any, params: any[]) => !params.map(p => lovValue(p)).includes((field.valueSourceType) ? lovValue(value) : value),
  EQUAL: (field: MdField, value: any, params: any[]) => ((field.valueSourceType) ? lovValue(value) : value) == lovValue(params[0]),
  NOTEQUAL: (field: MdField, value: any, params: any[]) => ((field.valueSourceType) ? lovValue(value) : value) != lovValue(params[0]),
  STARTSWITH: (field: MdField, value: string, params: string[]) => ((field.valueSourceType) ? lovLabel(value) : value).toLowerCase().startsWith(lovLabel(params[0]).toLowerCase()),
  NOTSTARTSWITH: (field: MdField, value: string, params: string[]) => !((field.valueSourceType) ? lovLabel(value) : value).toLowerCase().startsWith(lovLabel(params[0]).toLowerCase()),
  ENDSWITH: (field: MdField, value: string, params: string[]) => ((field.valueSourceType) ? lovLabel(value) : value).toLowerCase().endsWith(lovLabel(params[0]).toLowerCase()),
  NOTENDSWITH: (field: MdField, value: string, params: string[]) => !((field.valueSourceType) ? lovLabel(value) : value).toLowerCase().endsWith(lovLabel(params[0]).toLowerCase()),
  GREATERTHAN: (field: MdField, value: any, params: any[]) => ((field.valueSourceType) ? lovLabel(value) : value) > lovLabel(params[0]),
  GREATEROREQUALTHAN: (field: MdField, value: any, params: any[]) => ((field.valueSourceType) ? lovLabel(value) : value) >= lovLabel(params[0]),
  LESSTHAN: (field: MdField, value: any, params: any[]) => ((field.valueSourceType) ? lovLabel(value) : value) < lovLabel(params[0]),
  LESSOREQUALTHAN: (field: MdField, value: any, params: any[]) => ((field.valueSourceType) ? lovLabel(value) : value) <= lovLabel(params[0]),
  CONTAINS: (field: MdField, value: string, params: string[]) => ((field.valueSourceType) ? lovLabel(value) : value).toLowerCase().indexOf(lovLabel(params[0]).toLowerCase()) != -1,
  NOTCONTAINS: (field: MdField, value: string, params: string[]) => ((field.valueSourceType) ? lovLabel(value) : value).toLowerCase().indexOf(lovLabel(params[0]).toLowerCase()) == -1,
  LASTXDAYS: (field: MdField, value: any, params: any[]) => {
    let days: number = params[0] as number;
    let valueDate: Moment = moment(value);
    let systemDate: Moment = moment();
    return !valueDate.isBefore(systemDate.subtract(days, 'd')) && !valueDate.isAfter(systemDate);
  },
  NEXTXDAYS: (field: MdField, value: any, params: any[]) => {
    let days: number = params[0] as number;
    let valueDate: Moment = moment(value);
    let systemDate: Moment = moment();
    return !valueDate.isAfter(systemDate.add(days, 'd')) && !valueDate.isBefore(systemDate);
  },
  FROMTO: (field: MdField, value: any, params: any[]) => {
    value = ((field.valueSourceType) ? lovLabel(value) : value);
    return value >= lovLabel(params[0]) && value <= lovLabel(params[1]);
    },
  ISTRUE: (field: MdField, value: any, params: any[]) => value == true,
  ISFALSE: (field: MdField, value: any, params: any[]) => value == false,
  ISNULL: (field: MdField, value: any, params: any[]) => value == null,
  ISNOTNULL: (field: MdField, value: any, params: any[]) => value != null
};


export const ROW_HEIGHT = 28;

export interface SortItem {
  field: MdField;
  desc: boolean;
}


export interface HasAccessToEntity {
  getEntityRef: () => MdEntity;
}

@entityConfig()
export class GridLocationField extends DomainAncestor {
  gridHeaderLabel: string;
}

@entityConfig()
export class GridLocationFieldDto extends DomainAncestor {
  @observable gridLocationField: string;
  @observable @fieldInfo(LocaleLabel) locales?: ObservableMap<LocaleLabel[]>;

  constructor(json: any) {
    super(json);
  }

  public fromJson(json: any): this {
    super.fromJson(json, "locales");
    if (!this.locales) this.locales = new ObservableMap<LocaleLabel[]>();
    return this;
  }

}


@entityConfig()
export class GridLocation extends DomainAncestor implements HasAccessToEntity {

  gridLocation: string;
  label: string;

  entityName: string;

  $entityRef: MdEntity;

  gridConfigs: GridConfig[];

  implicitConditions: GridConfigCondition[];

  specialConditions: LovItem[];
  importConfigs: LovItem[];


  masterCondition: boolean;

  editable: boolean;

  assignedDocumentTypes: LovItem[];
  assignedActivityConfigs: LovItem[];

  $cached?: boolean;

  securable: LovItem;

  defaultPageSize: number;
  defaultScrollSize: number;
  defaultMaxColumnWidth: number;
  defaultHardRowLimit: number;
  defaultRefreshOnPage: boolean;
  defaultRefreshOnData: boolean;
  enableRefreshOnData: boolean;

  gridType: GRIDTYPE;

  @observable @fieldInfo(GridLocationField) fields?: ObservableMap<GridLocationField>;


  constructor(mdEntity: MdEntity, json: IDomainObject) {
    super(json);

    if (!this.specialConditions) this.specialConditions = [];
    if (!this.assignedDocumentTypes) this.assignedDocumentTypes = [];
    if (!this.assignedActivityConfigs) this.assignedActivityConfigs = [];

    this.$entityRef = mdEntity;
    this.gridConfigs = (this.gridConfigs) ? this.gridConfigs.map(gridConfigJson => new GridConfig(this, gridConfigJson)) : [];
    this.implicitConditions = (this.implicitConditions) ? this.implicitConditions.map(jsonGcc => new GridConfigCondition(this, jsonGcc)) : [];

    if (!this.fields) this.fields = new ObservableMap<GridLocationField>();
  }

  public fromJson(json: any): this {
    super.fromJson(json, "fields");
    return this;
  }

  public getEntityRef(): MdEntity {
    return this.$entityRef;
  }


  public replaceGridConfig(newGridConfig: GridConfig): void {
    for(let i = 0; i < this.gridConfigs.length; i++) {
      if (this.gridConfigs[i].gridId === newGridConfig.gridId) {
        this.gridConfigs[i] = newGridConfig;
        break;
      }
    }
  }

  public getActiveGridConfig(): GridConfig {
    let agc = first(this.gridConfigs.filter(gc => gc.defaultGrid));
    if (!agc) agc = first(this.gridConfigs);
    return agc;
  }

}



export interface GridCell {

  value: PRIMITIVE;

  /**
   * glyphicon or font-awasome classes (both for font and color)
   * or other classes for dressing value. If this class is present, inner span will be generated
   */
  glyph?: string;

  /**
   * classes to set on cell element
   */
  cssClass?: string;


  /**
   * tooltip text
   */
  tooltip?: string;

  /**
   * possible route param
   */
  dataId?: ID;
}


export class GridRow extends DomainAncestor {
  @observable rowId?: ID;

  @observable rowObject?: DomainAncestor;

  @observable rowHeight?: number;


  /**
   * List of cell values
   */
  @observable cells?: GridCell[];

  @observable aggregateType?: AGGREGATETYPE;

  /**
   * 0 - not expanded
   * 1 - expanding
   * 2 - expanded
   */
  @observable expanded?: number;

  constructor(json: any, grid: Grid) {
    super(json);
    this.rowHeight = ROW_HEIGHT;

    if (this.rowObject && !(this.rowObject instanceof DomainAncestor) ) {
      this.rowObject = DomainAncestor.getInstance<DomainAncestor>(grid.$gridLocationRef.entityName, this.rowObject).initAfterRead(false);
    }

  }

}

export interface PageMapIndex {
  plane: number;
  local: number;
}

export class GridData {

  /**
   * reference na gridConfig
   */
  config: number;

  grid: Grid;


  @observable readonly pageMap: ObservableMap<GridRow[]>;



  /**
   * aggregateRows
   */
  @observable aggregateRows: GridRow[];


  /**
   * total number of rows
   */
  @observable totalCount: number;

  @observable truncated: boolean;

  /**
   * page size
   */
  @observable pageSize: number;

  @observable start: number;

  // pouziva se pro cache busting
  @observable configDateCreated: Date;

  requestedPages = new Set<number>();

  constructor(grid: Grid, json: any) {
    this.grid = grid;
    this.config = json.config;
    this.totalCount = (json.totalCount != null) ? json.totalCount : 0;
    this.pageSize = (json.pageSize != null) ? json.pageSize : MAX_INTEGER;
    this.configDateCreated = json.configDateCreated;
    this.truncated = json.truncated;

    if (!this.aggregateRows) this.aggregateRows = [];
    this.pageMap = new ObservableMap<GridRow[]>();
  }

  public flatRows(sparse: boolean = false): GridRow[] {
    return this.pageMap.entries().sort(([k1, rs1], [k2, rs2]) => (parseInt(k1) - parseInt(k2))).reduce((acc, [key, rows]) => {
      return acc.concat(rows.slice(0));
    }, []);
  }

  public rowIds(): ID[] {
    const keyArray = this.pageMap.keys().map(str => parseInt(str)).sort();
    return keyArray.reduce((acc, page) => acc.concat(this.pageMap.get(page.toString()).map(row => row.rowId)), []);
  }

  public getRow(index: PageMapIndex) : GridRow {
    let rows = this.pageMap.get(index.plane.toString());
    return (rows) ? rows[index.local] : null;
  }

  public findRow(id: ID): GridRow {
    for(const rows of this.pageMap.values()) {
      let row = rows.find( gridRow => gridRow.rowId == id);
      if (row) return row;
    }
    return null;
  }

  public findRowIndex(rowId: ID) : PageMapIndex {
    for(let entry of this.pageMap.entries()) {
      let local  = entry[1].findIndex(row => row.rowId == rowId);
      if (local >= 0) {
        return {plane: parseInt(entry[0]), local};
      }
    }
    return null;
  }

  public isFirstIndex(index: PageMapIndex) : boolean {
    return (index.plane === 1 && index.local === 0);
  }

  public isLastIndex(index: PageMapIndex) : boolean {
    let sum = 0, _pageSize = this.pageSize;
    for(let i = 1; i < index.plane; i++) {
      sum += _pageSize;
      _pageSize *= 2;
    }
    sum += index.local;
    return (sum >= this.totalCount);
  }

  public nextIndex(index: PageMapIndex): PageMapIndex {
    let _pageSize = this.pageSize * Math.pow(2, index.plane - 1);
    if (index.local < (_pageSize - 1) ) {
      return {plane: index.plane, local: index.local + 1};
    } else {
      let newPlane = index.plane + 1;
      let rows = this.pageMap.get(newPlane.toString());
      return (rows) ? {plane: newPlane, local: 0} : null;
    }
  }

  public previousIndex(index: PageMapIndex): PageMapIndex {
    if (index.local > 0) {
      return {plane: index.plane, local: index.local - 1};
    } else {
      let newPlane = index.plane - 1;
      let rows = this.pageMap.get(newPlane.toString());
      return (rows) ? {plane: newPlane, local: (this.pageSize * Math.pow(2, newPlane - 1) - 1)} : null;
    }
  }

  // public isLastIndex(index: PageMapIndex) : boolean {
  //   if (!this.pageMap || !this.pageMap.size) return true;
  //
  //   let maxPage: number = Math.max(...this.pageMap.keys().map(str => parseInt(str)));
  //   return (maxPage === index.plane && index.local === (this.pageMap.get(maxPage.toString()).length - 1) );
  // }


  public initData(page: number, rows: GridRow[], aggregateRows: GridRow[]) {
    if (rows) {
      this.pageMap.set(page.toString(), observable.array(rows.map(rowJson => new GridRow(rowJson, this.grid))) );
    }
    if (aggregateRows) {
      this.mergeAggregates(aggregateRows, rows.length);
    }
  }

  public rowCount(): number {
    return this.pageMap.values().reduce((acc, rows) => (acc + rows.length), 0);
  }

  public isEmpty(): boolean {
    if (this.pageMap.size == 0) {
      return true;
    } else {
      for(const plane of this.pageMap.values()) {
        if (plane.length) return false;
      }
      return true;
    }
  }

  public addRow(page: string, row: GridRow) {
    let rowArray = this.pageMap.get(page);
    if (!rowArray) {
      rowArray = [];
      this.pageMap.set(page, rowArray);
    }
    rowArray.push(row);
    console.warn(rowArray)
    this.totalCount += 1;
  }

  public addFirstRow(page: string, row: GridRow) {
    let rowArray = this.pageMap.get(page);
    if (!rowArray) {
      rowArray = [];
      this.pageMap.set(page, rowArray);
    }
    rowArray.unshift(row)
    this.totalCount += 1;
  }



  public deleteRows(rows: GridRow[]) {
    for(const row of rows) {
      for(const plane of this.pageMap.values()) {
        const index = plane.indexOf(row);
        if (index !== -1) {
          plane.splice(index, 1);
          break;
        }
      }
    }
  }

  sliceSparse(firstIndex: PageMapIndex, lastIndex: PageMapIndex): GridRow[] {
    const pole: GridRow[] = [];

    let pageSize = this.pageSize * Math.pow(2, firstIndex.plane - 1);

    for(let page = firstIndex.plane; page <= lastIndex.plane; page++) {
      let plane = this.pageMap.get(page.toString());
      let _first = (page === firstIndex.plane) ? firstIndex.local : 0;
      let _last = (page === lastIndex.plane) ? (lastIndex.local + 1) : pageSize;
      if (plane) {
        pole.push(...plane.slice(_first, _last));
      } else {
        pole.push(...new Array( _last - _first ));
      }

      pageSize *= pageSize;
    }
    return pole;
  }


  private mergeAggregates(aggregateRows: GridRow[], newRowCount: number): void {
    if (!this.aggregateRows || !this.aggregateRows.length) {
      this.aggregateRows = aggregateRows;
    } else {
      for(let r = 0; r < this.aggregateRows.length; r++) {
        let thisAggRow = this.aggregateRows[r];
        let aggRow = aggregateRows[r];
        for(let i = 0; i < thisAggRow.cells.length; i++) {
          let thisCell = thisAggRow.cells[i];
          let newValue = aggRow.cells[i].value as number;
          let thisValue = thisCell.value as number;
          if (newValue) {
            switch (thisAggRow.aggregateType) {
              case AGGREGATETYPE.SUM:
                thisCell.value = thisValue + newValue;
                break;
              case AGGREGATETYPE.AVG:
                let thisRowCount = this.rowCount();
                thisCell.value = (thisValue * thisRowCount + newValue * newRowCount) / (thisRowCount + newRowCount) ;
                break;
              default:
                throw new Error(thisAggRow.aggregateType + " not supported");
            }
          }
        }
      }
    }
  }

}


export interface GridParams {
  gridId?: number;
  allRows?: boolean;
  rowid?: ID[];
  page?: number;
  searchTerm?: string;
  sort?: string[];
  masterId?: ID;
  specialConditions?: number[];
  filter?: string[];
  [key: string]: any;
  entity?: string;
  _includeSent?: boolean;
  _includeByAll?: boolean;
  _markSent?: boolean;
  _includeAll?: boolean;
  _byEmail?: boolean;
}

export default class Grid implements Movable {

  // string gridLocation
  public gridLocation: string;
  public $gridLocationRef: GridLocation;
  public $entityRef: MdEntity;



  // array of GridConfig
  public gridConfigs: GridConfig[];



  // aktualni [{field: MdField, desc: false/true}, ....]
  @observable public sortArray: SortItem[];

  // vybrany gridConfig
  @observable public activeGridConfig: GridConfig;


  // pole sirek sloupcu, spocitane na zaklade aktivniho gridConfigu a dat
  @observable.ref public gridWidths: number[][];

  // searchTerm
  @observable public searchTerm: string = '';


  // aktualni [gridConfigCondition, ....]
  @observable public conditionArray: GridConfigCondition[];

  // id na master entitu u seznamu na detailech
  @observable public masterId: ID;

  @observable public loading: boolean;


  // if true, grid is synced
  @observable public refreshData: boolean;



  @observable public showSelection: boolean = false;
  @observable public selectedRowIds: Set<ID> = new Set<ID>();
  @observable public deselectedRowIds: Set<ID> = new Set<ID>();
  @observable public selectedAllRows: boolean = false;
  @observable public lastClickedRow: ID;


  @observable public deletedRowIds: Set<ID>;

  @observable public errors: any[];

  @observable public specialConditions: LovItem[];

  @observable public data: GridData;

  @observable public $dirty: boolean;

  @observable public expandAllRows: boolean;

  public clientGrid: boolean;
  public clientGridDataPropertyName: string;


  constructor(gridLocation: GridLocation, masterId?: ID) {
    // string gridLocation
    this.gridLocation = gridLocation.gridLocation;
    this.$gridLocationRef = gridLocation;
    this.$entityRef = gridLocation.$entityRef;

    // zatim shallow copy
    this.gridConfigs = gridLocation.gridConfigs.slice();
    for(let gridConfig of this.gridConfigs) {
      if (!gridConfig.gridWidths) gridConfig.gridWidths = gridService.computeGridWidths(null, gridConfig);
    }

    this.masterId = masterId;

    this.assignActiveGridConfig(this.getActiveGridConfig());
  }


  @action assignActiveGridConfig(gridConfig: GridConfig) {
    this.activeGridConfig = gridConfig;
    if (gridConfig && gridConfig.sortColumns) {
      this.sortArray = gridConfig.sortColumns.map( (sc: GridConfigSort) => ({field: sc.field, desc: sc.sortOrder === 'DESC'}));
    }
    this.resetSpecialConditions(gridConfig);
    this.refreshData = gridConfig && gridConfig.refreshOnData && this.$gridLocationRef.enableRefreshOnData;
  }

  resetSpecialConditions(gridConfig: GridConfig) {
    this.specialConditions = (gridConfig && gridConfig.specialConditions) ? gridConfig.specialConditions.slice() : [];
  }

  getGridConfig(gridId: number): GridConfig {
    return this.gridConfigs.find(gc => gc.gridId == gridId);
  }

  @action deleteGridConfig(gridId) {
    remove(this.gridConfigs, gc => gc.gridId == gridId);
  }

  @action replaceGridConfig(gridConfig) {
    let index = findIndex(this.gridConfigs, gc => gc.gridId === gridConfig.gridId);
    if (index >= 0) {
      this.gridConfigs.splice(index, 1, gridConfig);
    } else {
      this.gridConfigs.push(gridConfig);
    }
  }


  /**
   * Vrati aktivni gridConfig, pokud je zadany gridId, zkousi hledat ten
   * @param gridId -nepovinny paramter
   */
  getActiveGridConfig(gridId?: number): GridConfig {
    let agc;
    if (gridId) {
      agc = this.gridConfigs.find(gc => gc.gridId == gridId);
    }

    if (!agc) {
      agc = this.activeGridConfig;
    }

    if (!agc) {
      agc = first(this.gridConfigs.filter(gc => gc.defaultGrid));
    }

    if (!agc) {
      agc = first(this.gridConfigs);
    }

    return agc;
  }

  get activeGridWidths() {
    return (this.gridWidths) ? this.gridWidths : this.activeGridConfig.gridWidths;
  }

  /**
   * vrati pole stringu [fieldName1_ASC, fieldName2_DESC, ..]
   */
  get sort(): string[] {
    return (this.sortArray) ? this.sortArray.map(o => o.field.fieldName + ((o.desc) ? "_DESC" : "_ASC")) : null;
  }

  /**
   * setne sortArray - [ {field: field1Ref, desc: true/false}, ... ]  ze stringu (nebo pole stringu) fieldName1_ASC
   */
  set sort(sortValue) {
    if (sortValue) {
      if (isString(sortValue)) {
        sortValue = [sortValue];
      }
      this.sortArray = sortValue.map(str => {
        let fieldName = str.replace(/(_ASC$)|(_DESC$)/i, "");
        return {field: this.getField(fieldName), desc: (str.endsWith("_DESC"))};
      });
    } else {
      this.sortArray = [];
    }
  }

  getField(fieldName: string) : MdField {
    return (this.$entityRef) ? this.$entityRef.getField(fieldName) : this.getActiveGridConfig().columns.find(c => c.field.fieldName === fieldName).field;
  }

  /**
   * vytvori z conditionArray (pole gridConfigCondition) ->  { filter: [fieldName1_EQUAL, fieldName2_IN, ...],  fieldName1: [value11, value12, ...], fieldName2: [value21, value22, ...], .... }
   */
  getConditionQueryObject() {
    if (isEmpty(this.conditionArray)) {
      return {};
    } else {
      let queryObject = {filter: []};
      for (let gcc of this.conditionArray) {
        queryObject.filter.push(gcc.columnName + "_" + gcc.operator);
        queryObject[gcc.columnName] = (gcc.values) ? gcc.values.map(v => lovValue(v)).filter((v, i, a) => a.indexOf(v) === i) : gcc.values;
      }
      return queryObject;
    }
  }

  getTotalCount() {
    if (typeof this.data === 'undefined') {
      return 'undefined';
    } else if (!this.data) {
      return 'null';
    } else {
      return this.data.totalCount;
    }
  }


  /**
   * setne conditionArray (pole gridConfigCondition) z { filter: [fieldName1_EQUAL, fieldName2_IN, ...],  fieldName1: [value11, value12, ...], fieldName2: [value21, value22, ...], .... }
   */
  @action setConditionQueryObject(query) {
    console.log("query", query);
    if (isEmpty(query.filter)) {
      this.conditionArray = null;
    } else {
      this.conditionArray = [];
      for (let oper of query.filter) {
        let gcc = new GridConfigCondition(this.$gridLocationRef, null);
        let [fieldName, operator] = splitByLastSubstring(oper);
        gcc.field = this.getField(fieldName);
        gcc.operator = operator as FILTEROPERATOR;
        gcc.values = query[fieldName];
        if (gcc.values) gcc.values = gcc.values.filter((v, i, a) => a.indexOf(v) === i); // uniq
        this.conditionArray.push(gcc);
      }
    }
  }



  @action addRow(rowObject) {
    if (!this.data) {
      this.data = new GridData(this, {});
    }
    this.data.addRow("1", new GridRow({rowId: null, cells: [], rowObject}, this));
    this.$dirty = true;
  }

  @action addFirstRow(rowObject) {
    if (!this.data) {
      this.data = new GridData(this, {});
    }
    this.data.addFirstRow("1", new GridRow({rowId: null, cells: [], rowObject}, this));
    this.$dirty = true;
  }





  @action addDeletedId(rowIds) {
    if (!this.deletedRowIds) this.deletedRowIds = new Set();

    if (rowIds != null) {
      rowIds.forEach(rowId => this.deletedRowIds.add(rowId));
    }
  }

  getEditedRows(): DomainAncestor[] {
    return this.data.flatRows().filter(item => item.rowObject.$dirty).map(item => item.rowObject);
  }



  isSelectedRows() : boolean {
    return this.data && !this.data.isEmpty() && (!this.showSelection || (this.selectedRowIds && this.selectedRowIds.size > 0));
  }

  getSelectedCount(): number {
    // pokud jsou zobrazeny select checkboxy a neni selectAllRows -> selectedRowIds size
    //                                       a je selectAllRows - totalCount - deselectRowIds size
    //  nejsou select checkboxy ->  totalCount

    if (this.showSelection) {
      if (this.selectedAllRows) {
        return (this.data.totalCount - this.deselectedRowIds.size);
      } else {
        return this.selectedRowIds.size;
      }
    } else {
      return this.data.totalCount;
    }
  }

  /**
   * Pozor, pouze pro nestrankove gridy
   * @returns {GridRow[]}
   */
  getSelectedRows() : GridRow[] {
    if (!this.data) return [];
    let rows = this.data.flatRows();
    return (this.showSelection) ? rows.filter( gridRow => this.selectedRowIds.has(gridRow.rowId)) : rows;
  }


  findRow(id: ID): GridRow {
    return (this.data) ? this.data.findRow(id) : null;
  }


  @action search(dataObject: IDomainObject, refresh: boolean = true, page: number = 1, keepSelection: boolean = false): Promise<any> {
    if (!this.activeGridConfig) {
      console.debug('GridComp#search(): activeGridConfig not set, ending for grid ' + this.gridLocation);
      return Promise.resolve(null);
    }

    if (!keepSelection) {
      this.selectedRowIds = new Set();
      this.deselectedRowIds = new Set();
      this.selectedAllRows = false;
    }

    if (this.$gridLocationRef.masterCondition && !this.masterId) {
      console.debug('GridComp#search(): masterId is not set, ending for grid ' + this.gridLocation);
      return Promise.resolve(null);
    }


    if (refresh) this.loading = true;


    return gridService.search(this, refresh, page);
  };



  createParams(): GridParams {
    let params: any = Object.assign({
      searchTerm: (this.searchTerm) ? this.searchTerm.trim() : this.searchTerm,
      sort: this.sort,
      masterId: this.masterId
    }, this.getConditionQueryObject());

    if (this.specialConditions && this.specialConditions.length) {
      params.specialConditions = this.specialConditions.map(lov => lov.value);
    }
    return params
  }


  createFullParams(anyParams?: object): GridParams {
    // pokud jsou zobrazeny select checkboxy a neni selectAllRows -> selectedRowIds
    //                                       a je selectAllRows - search & deselectRowIds
    //  nejsou select checkboxy ->  search


    if (this.showSelection) {
      if (this.selectedAllRows) {
        return Object.assign({}, anyParams, this.createParams(), {allRows: true, rowid: Array.from(this.deselectedRowIds), gridId: this.activeGridConfig.gridId})
      } else {
        return Object.assign({}, anyParams, {rowid: Array.from(this.selectedRowIds), gridId: this.activeGridConfig.gridId});
      }
    } else {
      return Object.assign({}, anyParams, this.createParams(), {allRows: true, gridId: this.activeGridConfig.gridId})
    }
  }

  public updateGridConditions(condition: GridConfigCondition, mdField: MdField): void {
    if (!this.conditionArray) this.conditionArray = [];

    let oldConditionIndex = this.conditionArray.findIndex(gcc => gcc.columnName === mdField.fieldName);

    if (condition) {
      if (oldConditionIndex >= 0) {
        this.conditionArray.splice(oldConditionIndex, 1, condition);
      } else {
        this.conditionArray.push(condition);
      }
    } else {
      if (oldConditionIndex >= 0) {
        this.conditionArray.splice(oldConditionIndex, 1);
      }
    }
  }



  public getSuperFilters(): GridConfigCondition[] {
    return (!this.conditionArray) ? [] : this.conditionArray.filter(gcc => gcc.superFilter);
  }

  public initSuperFilter() {
    console.log("initSuperFilter conditionArray = ", this.conditionArray);
    if (!this.conditionArray) this.conditionArray = [];

    this.activeGridConfig.getSuperFilters().forEach(gcc => {
      let oldConditionIndex = this.conditionArray.findIndex(oldGcc => oldGcc.columnName === gcc.field.fieldName);
      if (oldConditionIndex === -1) {
        this.conditionArray.push(gcc.clone());
      } else {
        let condition = this.conditionArray[oldConditionIndex].clone();
        condition.superFilter = true;
        this.conditionArray.splice(oldConditionIndex, 1, condition);
      }
    });
    console.log("initSuperFilter size = " + this.getSuperFilters().length);
    this.getSuperFilters().forEach(gcc => gcc.convertToLov());
  }


  getBlockName(): string {
    return this.gridLocation;
  }

  isEditable(): boolean {
    return this.$gridLocationRef.editable;
  }

  isClientBlock(): boolean {
    return this.clientGrid;
  }

  get label(): string {
    return this.$gridLocationRef.label;
  }

  selectAllRows(selected: boolean) : void {
    this.selectedAllRows = selected;
    if (selected) {
      this.selectedRowIds = new Set(this.data.rowIds());
      this.deselectedRowIds = new Set();
    } else {
      this.selectedRowIds = new Set();
      this.deselectedRowIds = new Set();
    }
  }

}


type TFn<ROWO extends DomainAncestor> = (rowObject: ROWO) => string;
type TFnMap<ROWO extends DomainAncestor> = Record<string, TFn<ROWO>>;

export class ClientGrid<ROWO extends DomainAncestor> extends Grid {

  tooltipFnMap: TFnMap<ROWO>;
  cssFnMap: TFnMap<ROWO>;

  constructor(gridLocation: GridLocation, clientGridDataPropertyName: string,  masterId?: ID, tooltipFnMap?: TFnMap<ROWO>, cssFnMap?: TFnMap<ROWO>) {
    super(gridLocation, masterId);
    this.clientGrid = true;
    this.clientGridDataPropertyName = clientGridDataPropertyName;
    this.tooltipFnMap = tooltipFnMap;
    this.cssFnMap = cssFnMap;
  }

  customFilterFunction: (dataObject: DomainAncestor, rowObject: DomainAncestor) => boolean;

  generateGridData(dataObject: DomainAncestor) {
    if (!this.activeGridConfig) {
      console.debug('ClientGrid#search(): activeGridConfig not set, ending');
      return;
    }

    console.debug("generateGridData dataObject = %O", dataObject);

    const columns = this.activeGridConfig.columns;

    const rowObjects: ROWO[] = dataObject[this.clientGridDataPropertyName];
    const rows = [];
    for (let rowObject of rowObjects) {
      if (!this.clientFilterRow(rowObject) || (this.customFilterFunction && !this.customFilterFunction(dataObject, rowObject)) ) continue;
      let cells = [];

      for (let column of columns) {
        let fieldName = column.field.fieldName;

        let value = (column.field.valueSourceType) ? lovLabel(rowObject[fieldName]) : rowObject[fieldName];
        let tooltip = (this.tooltipFnMap && this.tooltipFnMap[fieldName]) ? this.tooltipFnMap[fieldName](rowObject) : undefined;
        let cssClass = (this.cssFnMap && this.cssFnMap[fieldName]) ? this.cssFnMap[fieldName](rowObject) : undefined;

        let cell = {
          value,
          tooltip,
          cssClass
        };

        cells.push(cell);
      }


      let row = {
        rowId: rowObject.id,
        rowObject: rowObject,
        cells: cells
      };

      rows.push(row);
    }

    this.data = new GridData(this, {});
    this.data.initData(1, rows, null);
    this.data.totalCount = this.data.rowCount();

    if (this.activeGridConfig) {
      this.gridWidths = gridService.computeGridWidths(this.data, this.activeGridConfig);
    }

  }

  @action clientSort() {
    // sortArray : [{field: MdField, desc: false/true}, ....]

    if (!this.data || !this.data.pageMap) return;

    if (this.data.pageMap.size > 1) throw new Error("ClientSort must run on pageMap size = 1");

    let rows = this.data.pageMap.get("1");

    console.log("BEFOR SORT", rows);
    if (rows && rows.length && this.sortArray && this.sortArray.length) {
      let lc = loc.lang;
      rows = rows.sort((r1, r2) => {
        for (let sortSpec of this.sortArray) {
          let value1 = (sortSpec.field.valueSourceType) ? lovLabel(r1.rowObject[sortSpec.field.fieldName]) : r1.rowObject[sortSpec.field.fieldName];
          let value2 = (sortSpec.field.valueSourceType) ? lovLabel(r2.rowObject[sortSpec.field.fieldName]) : r2.rowObject[sortSpec.field.fieldName];
          let res = (sortSpec.desc) ? compare(value2, value1, lc) : compare(value1, value2, loc);
          if (res !== 0) {
            return res;
          }
        }
        return 0;
      });
      this.data.pageMap.set("1", observable.array(rows));
    }
    console.log("AFTER SORT", rows);
  }

  clientFilterRow(rowObject: DomainAncestor): boolean {
    let result = true;

    if (this.searchTerm) {
      result = this.activeGridConfig.columns.filter(gcCol => gcCol.field.searchTermType).reduce((acc, gcCol) => {
        if (acc) return true;
        return this.filterCondition(rowObject, gcCol.field, gcCol.field.searchTermType, [this.searchTerm]);
      }, false);
    }

    if (result && !isEmpty(this.conditionArray)) {
      result = this.conditionArray.reduce((acc, co) => {
        if (!acc) return false;
        return this.filterCondition(rowObject, co.field, co.operator, co.values);
      }, true);
    }

    console.log("clientFilterRow %O => %s", rowObject, result);
    return result;
  }

  private filterCondition(rowObject: DomainAncestor, field: MdField, operator: FILTEROPERATOR, params: any[]): boolean {
    const fn: TFilterFunction = FILTEROPERATOR_MAP[operator];
    return fn(field, rowObject[field.fieldName], params);
  }

  @action search(dataObject: DomainAncestor, refresh: boolean = true): Promise<any> {


    this.generateGridData(dataObject);

    this.clientSort();
    return Promise.resolve(null);
  }


}



// WEBPACK FOOTER //
// ./src/core/grid/domain/grid.ts